import {GRANITE_2, GRANITE_3, GRANITE_6, SNOW_BLIND} from '@gaia/colors';
import useWeatherAPI from '@gaia/hooks/useWeatherAPI';
import {WeatherErrorIcon24px} from '@gaia/icons/weather/icons';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {Body2} from '@map/sidebars/SidebarTypography';
import {ArrowForwardIos} from '@mui/icons-material';
import {IconButton, ListItem, ListItemButton, ListItemProps, ListItemText} from '@mui/material';
import {styled} from '@mui/material/styles';

import {code03_24} from './icons';
import {ForecastChip, PrecipitationChip, WindChip} from './WeatherChip';

const NavArrow = styled(IconButton)(() => ({
    backgroundColor: SNOW_BLIND,
    border: 0,
    color: GRANITE_6,
    height: 32,
    width: 32,
    justifySelf: 'flex-end',
    marginLeft: theme.spacing(1),
    '&:hover, .Mui-focusVisible &': {
        backgroundColor: `rgba(0, 0, 0, ${theme.palette.action.hoverOpacity})`,
        color: GRANITE_3,
    },
    '& .MuiSvgIcon-root': {
        fontSize: 14,
    },
}));

const weatherContainerStyle = {
    paddingTop: 0,
    paddingBottom: 0,
};

export interface WeatherSummaryProps extends ListItemProps {
    coordinates: number[];
    onSidebarChange: React.Dispatch<React.SetStateAction<SidebarState>>;
    sidebar: SidebarState;
}

function WeatherTitle() {
    return (
        <Body2
            component='h3'
            style={{
                color: GRANITE_2,
                marginBottom: theme.spacing(0.5),
            }}
        >
            Current Weather
        </Body2>
    );
}

export default function WeatherSummary({
    coordinates,
    onSidebarChange,
    sidebar,
    ...props
}: WeatherSummaryProps): JSX.Element {
    const {unitSystem: units} = useUser();
    const {data: current, error, isLoading} = useWeatherAPI({type: 'current', coordinates});

    return error && !isLoading ? (
        <ListItem
            component='div'
            disableGutters
            sx={weatherContainerStyle}
            style={{...props.style}}
        >
            <ListItemText
                disableTypography
                primary={<WeatherTitle />}
                secondary={
                    <Body2 style={{color: GRANITE_3, lineHeight: 32 / 14}}>
                        <WeatherErrorIcon24px
                            height='24'
                            width='24'
                            style={{
                                verticalAlign: 'middle',
                            }}
                        />
                        Weather not available for this location
                    </Body2>
                }
            />
        </ListItem>
    ) : current ? (
        <ListItemButton
            disableGutters
            sx={weatherContainerStyle}
            onClick={() => {
                onSidebarChange({
                    type: 'weather',
                    coordinates,
                    prevSidebar: sidebar,
                    currentConditions: current,
                });
                ampli.openSidebar({
                    type: 'weather',
                    location: sidebar.type,
                    latitude: coordinates[1],
                    longitude: coordinates[0],
                    units,
                });
            }}
            style={{
                ...props.style,
                background: SNOW_BLIND,
            }}
            // this plugin does not support aria-description as of 6.5.1
            // eslint-disable-next-line jsx-a11y/aria-props
            aria-description='Current weather at marked location. Click for forecast.'
        >
            <ListItemText
                disableTypography
                primary={<WeatherTitle />}
                secondary={
                    <div style={{display: 'flex'}}>
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <ForecastChip
                                icon={current.icon}
                                condition={current.condition}
                                temperature_max={current.temperature}
                            />
                            <PrecipitationChip
                                precipitation_type={current.precipitation_type}
                                precipitation_percent={current.precipitation_percent}
                            />
                            <WindChip
                                wind_avg={current.wind_avg}
                                wind_direction={current.wind_direction}
                                wind_gust={current.wind_gust}
                            />
                        </div>
                        <NavArrow>
                            <ArrowForwardIos />
                        </NavArrow>
                    </div>
                }
            />
        </ListItemButton>
    ) : (
        <ListItem
            component='div'
            disableGutters
            sx={weatherContainerStyle}
            style={{...props.style}}
        >
            <ListItemText
                disableTypography
                primary={<WeatherTitle />}
                secondary={
                    <Body2 style={{color: GRANITE_3, lineHeight: 32 / 14}}>
                        <img
                            src={code03_24}
                            alt=''
                            height='24'
                            width='24'
                            style={{
                                marginRight: theme.spacing(0.5),
                                verticalAlign: 'middle',
                                position: 'relative',
                                bottom: 2,
                            }}
                        />
                        Loading weather...
                    </Body2>
                }
            />
        </ListItem>
    );
}
